import { useState } from 'react';
import type { Package } from '@mypoz/shared';
import { gramsToKg, isPackageLocked } from '@mypoz/shared';
import { findEntry, useTranslation } from '@mypoz/i18n';
import { cn, Icon } from '@mypoz/ui';

import { PackageStatusChip } from '@/features/packages/components/PackageStatusChip.js';
import { overdueStorageDays } from '@/features/packages/storageRisk.js';
import { useWarehouse } from '@/features/warehouses/index.js';
import { usePackages } from '@/features/packages/context.js';
import { PackagesRequestError } from '@/features/packages/api.js';
import { DELETE_PACKAGE_ERROR, isUnconfirmedFailure } from '@/features/packages/dictionary.js';
import { useToast } from '@/features/toast/index.js';
import { actionLinkClass, buttonClass } from '@/lib/styles.js';
import { useFormat } from '@/lib/format.js';

export interface PackageListRowProps {
  package: Package;
  /** Passed down rather than read with `new Date()` per row, so every row ages off the same clock. */
  now: Date;
  /** Whether this row is currently checked. Ignored when the row isn't selectable — see below. */
  selected: boolean;
  /**
   * Absent rather than a no-op handler for a locked package: a package already tied to an order is
   * fixed history on this list (same reasoning as the Edit/View split below), so there is nothing
   * a selection could be *for*. Omitting the prop is what makes the checkbox itself disappear
   * instead of rendering disabled — one fact driving both decisions.
   */
  onToggleSelect?: () => void;
  /**
   * Opens MYP-33's slide-over read-only. Offered on every row, not only locked ones: reading what
   * the warehouse recorded about a parcel is a different thing from changing it, and before this
   * the only way to see an editable package's details was to open the edit form and read around
   * the inputs.
   */
  onView: () => void;
  /**
   * Opens the same slide-over as a form. Absent for a locked package, which has nothing editable —
   * the same reasoning that drops `onToggleSelect` and Delete for those rows.
   */
  onEdit?: () => void;
}

/**
 * One hairline row — this component is the row half of MYP-35's spec, applied here rather than as
 * a separate screen since there is nothing to show it against but this list.
 *
 * MYP-35 gives every row a real header line instead of the fixed-width `sm:w-56`/`sm:w-40` columns
 * it had before: the checkbox and tracking number on the left, Edit/View (and now MYP-36's Delete)
 * pinned to the right. Those old fixed columns never shrank, so at in-between desktop widths
 * (roughly 640–950px) the flex-1 tracking column did all the shrinking for them — that's what
 * produced the overlapping, unreadable rows the ticket reported. Everything else (item line,
 * service tags, warehouse/weight, the overdue warning, the status chip) now flows in its own
 * wrapping block under the header instead of competing columns, so a narrow screen just wraps it
 * onto more lines rather than crushing its neighbour.
 */
export function PackageListRow({
  package: pkg,
  now,
  selected,
  onToggleSelect,
  onView,
  onEdit,
}: PackageListRowProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const toast = useToast();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // MYP-99: the row owns its own delete — the confirm (MYP-36), the request, and a refusal shown
  // right here under the row it concerns, rather than a toast that has scrolled away from it.
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<{ code: string; detail: string } | undefined>(
    undefined,
  );

  const warehouse = useWarehouse(pkg.warehouseId);
  // No warning at all until the warehouse list has loaded, rather than one computed against a
  // free period nobody has stated yet — see `overdueStorageDays`.
  const overdueDays = overdueStorageDays(pkg, now, warehouse?.freeStorageDays);
  const locked = isPackageLocked(pkg);

  // Names, not translated labels — a warehouse service's `name` is data (MYP-34), the same way
  // `pkg.itemNameEn` and a warehouse's own `label` are, not UI chrome that goes through `t()`.
  // In practice this renders nothing yet: `package_list` never returns a package's chosen
  // services, so `serviceCodes` is always empty (see `normalisePackage`).
  const { servicesFor, deletePackage } = usePackages();
  const warehouseServices = servicesFor(pkg.warehouseId);
  const serviceTags = pkg.serviceCodes.map((id) => {
    const service = warehouseServices.find((item) => item.id === id);
    return { code: id, label: service?.name ?? id };
  });

  /*
    Three endings. Success: the list is re-read and this row simply stops existing, so the only
    confirmation that can outlive it is a toast. A clean refusal (0, 2, -2): nothing changed, the
    list is left alone, and the reason is shown inline under this row. A lost answer: the list is
    being re-read — which unmounts this row — so that outcome is also a toast, and says plainly
    that nobody knows yet whether the delete happened.
  */
  async function handleDeleteConfirmed() {
    if (deleting) return;
    setDeleting(true);
    setDeleteError(undefined);
    try {
      await deletePackage(pkg.id);
      toast.show(t('packages.list.delete.deleted', { tracking: pkg.trackingNumber }));
    } catch (cause) {
      console.error('[packages] delete_package failed', cause);
      const code = cause instanceof PackagesRequestError ? cause.code : 'UNKNOWN';
      const detail = cause instanceof PackagesRequestError ? cause.detail : '';
      if (isUnconfirmedFailure(code)) {
        toast.show(t('packages.list.delete.error.unconfirmed'));
      } else {
        setDeleteError({ code, detail });
      }
      setDeleting(false);
      setConfirmingDelete(false);
    }
  }

  // A parcel already at the warehouse is a physical thing on a shelf, not just a record — the
  // confirm says so before it goes, rather than treating it like one still in transit.
  const atWarehouse = pkg.status === 'arrived' || pkg.status === 'in_warehouse';
  const deleteErrorEntry = deleteError
    ? findEntry(DELETE_PACKAGE_ERROR, deleteError.code)
    : undefined;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 px-5 py-4 transition-colors sm:px-6',
        selected && onToggleSelect ? 'bg-brand-soft/40' : undefined,
      )}
    >
      {/* Header line (MYP-35): tracking number on the left, actions pinned to the right, on every
          row and every width — the fix for what MYP-35 reported. */}
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-center gap-3">
          {onToggleSelect ? (
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              aria-label={t('packages.list.selectRow', { tracking: pkg.trackingNumber })}
              className="size-4 shrink-0 border-line-strong text-brand accent-brand"
            />
          ) : (
            // Locked rows have no checkbox, but keep the placeholder so tracking numbers stay
            // aligned with the selectable rows above and below them.
            <span aria-hidden="true" className="size-4 shrink-0" />
          )}
          <p className="truncate text-sm font-semibold text-ink tabular-nums">
            {pkg.trackingNumber}
          </p>
        </div>

        {confirmingDelete ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <p className="text-sm text-ink">
              {atWarehouse
                ? t('packages.list.delete.confirmAtWarehouse')
                : t('packages.list.delete.confirm')}
            </p>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              disabled={deleting}
              className={buttonClass('secondary', 'disabled:opacity-50')}
            >
              {t('packages.list.delete.keep')}
            </button>
            <button
              type="button"
              onClick={() => void handleDeleteConfirmed()}
              disabled={deleting}
              aria-busy={deleting || undefined}
              className={buttonClass('danger', 'disabled:cursor-not-allowed disabled:opacity-50')}
            >
              {deleting ? t('packages.list.delete.deleting') : t('packages.list.delete.confirmed')}
            </button>
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-4">
            {/* View first, and on every row: it is the one action that is always available and
                never changes anything, so it reads as the safe default before Edit and Delete. */}
            <button type="button" onClick={onView} className={actionLinkClass()}>
              {t('packages.list.action.view')}
            </button>
            {onEdit ? (
              <button type="button" onClick={onEdit} className={actionLinkClass()}>
                {t('packages.list.action.edit')}
              </button>
            ) : null}
            {/* Delete follows the same lock rule Edit does — a package already tied to an order is
                fixed history, nothing here is deletable. */}
            {locked ? null : (
              <button
                type="button"
                onClick={() => {
                  setDeleteError(undefined);
                  setConfirmingDelete(true);
                }}
                className={actionLinkClass('text-danger hover:text-danger hover:opacity-80')}
              >
                {t('packages.list.action.delete')}
              </button>
            )}
          </div>
        )}
      </div>

      {deleteError ? (
        <div role="alert" className="ml-7 rounded-md border border-danger/30 bg-danger/5 px-3 py-2">
          <p className="text-sm text-danger">
            {deleteErrorEntry
              ? t(deleteErrorEntry.labelKey)
              : t('packages.list.delete.error.unknown', { code: deleteError.code })}
          </p>
          {deleteError.detail && (!deleteErrorEntry || deleteError.code !== '-2') ? (
            <p className="mt-1 text-xs text-muted">
              {t('packages.list.delete.vendorSaid', { detail: deleteError.detail })}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Everything else, indented to sit under the tracking number and free to wrap. */}
      <div className="pl-7">
        <p className="truncate text-sm text-muted">
          {t('packages.list.itemLine', { item: pkg.itemNameEn, count: pkg.quantity })}
          {pkg.orderNumber ? (
            <>
              {' · '}
              <span className="tabular-nums">{pkg.orderNumber}</span>
            </>
          ) : null}
        </p>

        {serviceTags.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {serviceTags.map((tag) => (
              <span
                key={tag.code}
                className="rounded bg-brand-soft px-1.5 py-0.5 text-xs font-semibold text-brand"
              >
                {tag.label}
              </span>
            ))}
          </div>
        ) : null}

        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="text-sm text-ink">
            {warehouse?.label ?? pkg.warehouseId}
            {' · '}
            {pkg.weightGrams !== undefined
              ? t('orders.newOrder.weightKg', { value: fmt.number(gramsToKg(pkg.weightGrams), 1) })
              : t('orders.newOrder.awaitingIntake')}
          </p>
          {overdueDays !== undefined ? (
            <p className="flex items-center gap-1 text-xs font-medium text-danger">
              <Icon name="warning" size={14} fill />
              {t('packages.list.storageOverdue', { days: overdueDays })}
            </p>
          ) : null}
          <PackageStatusChip status={pkg.status} label={pkg.statusLabel} />
        </div>
      </div>
    </div>
  );
}
